import { useEffect, useState } from 'react';
import { hooks, constants as sdkConstants } from '@lifesize/clients.sdk';
import { defineMessages } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';
import buddyIcon from 'assets/images/buddy_128x128.png';
import { logger } from 'utils/logger';
import { messagesSelector } from 'observers/utils/intl';
import { useEventListener } from 'hooks/useEventListener';
import { setPillNotificationWithTimeout } from 'actions/Notification';
import { environmentSelectors } from '@lifesize/nucleus';
import _get from 'lodash/get';

const defaultNotificationOptions = {
  icon: buddyIcon,
  timeout: 0, // no timeout
  priority: 100, // highest
  requireInteraction: true
};

const messages = defineMessages({
  call: {
    id: 'notification.call.title',
    defaultMessage: 'Incoming Call From'
  },
  presentationStopped: {
    id: 'notification.presentationStopped',
    defaultMessage: 'The moderator has stopped your presentation.'
  },
  unknownUser: {
    id: 'unknownUser',
    defaultMessage: 'Unknown User'
  }
});

const notifications = new Map<string, Notification>();

const notificationMessagesSelector = (state) => messagesSelector(state, messages);

const createNotification = async (
  title: string,
  notificationOptions?: NotificationOptions
): Promise<undefined | Notification> => {
  if (!Notification) {
    logger.info('This browser does not support desktop notification');
    return;
  }

  if (Notification.permission !== 'granted') {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      logger.info('User did not allow notifications.');
      return;
    }
  }

  const notification = new Notification(title, notificationOptions);
  notification.onclick = () => {
    window.focus();
    notification.close();
  };
  return notification;
};

const processIncomingCalls = async (incomingCalls, notificationMessages) => {
  if (incomingCalls.length) {
    const newNotifications: Promise<void>[] = [];
    incomingCalls.forEach((call) => {
      const callId = call.callId;
      if (notifications.get(callId)) {
        // The notification is already displayed.
        return;
      }

      const addNewNotification = async () => {
        const displayName = call?.from || call?.displayName || notificationMessages.unknownUser;
        const title = notificationMessages.call;
        const notificationOptions = {
          ...defaultNotificationOptions,
          body: displayName
        };
        const notificationToAdd = await createNotification(title, notificationOptions);
        if (notificationToAdd) {
          notifications.set(callId, notificationToAdd);
        }
      };

      newNotifications.push(addNewNotification());
    });

    await Promise.all(newNotifications);
  }

  notifications.forEach((value, key) => {
    const incomingCallExists = incomingCalls.find((call) => key === call.callId);
    if (!incomingCallExists) {
      value.close();
      notifications.delete(key);
    }
  });
};

const NotificationComponent = () => {
  const incomingCalls = hooks.useIncomingCalls();
  const notificationMessages = useSelector(notificationMessagesSelector);
  const presentationTokenState = hooks.usePresentationToken();
  const [visible, setVisible] = useState(document.hasFocus());
  const [presentationNotification, setPresentationNotification] = useState<Notification | undefined>(undefined);
  const dispatch = useDispatch();
  const environmentSelector = useSelector((state) => environmentSelectors.platform(state));
  const pillMessage = useSelector((state) => _get(state, 'app.notification.pill'));

  useEventListener(window, 'focus', () => {
    setVisible(true);
  });
  useEventListener(window, 'blur', () => {
    setVisible(false);
  });

  useEffect(() => {
    if (presentationTokenState === sdkConstants.PRESENTATION_TOKEN_STATE_REVOKED) {
      const showPresentationNotification = async () => {
        const message = notificationMessages.presentationStopped;
        if (message !== pillMessage) {
          if (!visible) {
            const newPresentationNotification = await createNotification(message, {
              icon: buddyIcon,
              requireInteraction: false
            });
            setPresentationNotification(newPresentationNotification);
          }
          dispatch(setPillNotificationWithTimeout(message));
        }
      };

      if (!presentationNotification) {
        showPresentationNotification();
      }
    } else {
      setPresentationNotification(undefined);
    }
  }, [
    presentationTokenState,
    visible,
    dispatch,
    notificationMessages.presentationStopped,
    presentationNotification,
    pillMessage
  ]);

  useEffect(() => {
    if ((visible && !environmentSelector.isDesktop) || !incomingCalls.length) {
      notifications.forEach((value, key) => {
        value.close();
        notifications.delete(key);
      });
    } else {
      processIncomingCalls(incomingCalls, notificationMessages);
    }
  }, [environmentSelector.isDesktop, incomingCalls, notificationMessages, visible]);

  return null;
};

export default NotificationComponent;
